import { useState } from "react"
import { useLocalStorage } from "src/utils/useLocalStorage"
import { Message, MessageType, Suggestion, Provider } from "../km"
import { useSnackbar } from "src/providers/SnackbarProvider"
import { useLogger } from "src/providers"

interface UseChatStateProps {
  storageKey: string
  defaultMessage: () => string
  customSelection?: (suggestions: Suggestion | undefined, messages: Message[]) => Provider | undefined
}

export const useChatState = ({ storageKey, defaultMessage, customSelection }: UseChatStateProps) => {
  const { logger } = useLogger()
  const { snackbarShowMessage } = useSnackbar()

  const [query, setQuery] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)

  const [messageState, setMessageState, clearMessageState] = useLocalStorage<{
    messages: Message[]
    history: string[]
  }>(
    storageKey,
    {
      messages: [
        {
          id: "0",
          message: defaultMessage(),
          type: "apiMessage" as MessageType
        }
      ],
      history: []
    },
    (value: { messages: Message[]; history: string[] }) => value.history.length > 0
  )

  const handleSubmit = async (e: any, postQuestion: (question: string, history: string[]) => Promise<any>) => {
    e.preventDefault()

    const question = query.trim()

    setMessageState((state) => ({
      ...state,
      messages: [
        ...state.messages,
        {
          id: "",
          type: "userMessage",
          message: question
        }
      ]
    }))
    setLoading(true)
    setQuery("")

    postQuestion(question, messageState.history)
      .then((data) => {
        const selectedProvider = customSelection ? customSelection(data.suggestions, messageState.messages) : undefined
        setMessageState((state) => ({
          history: [...state.history, question, data.answer],
          messages: [
            ...state.messages,
            {
              id: data.id || "",
              type: "apiMessage",
              message: data.answer,
              sourceDocs: data.context,
              provider: selectedProvider
            }
          ]
        }))
      })
      .catch((error) => {
        snackbarShowMessage("An error occurred while fetching the data. Please try again.", "error")
        logger.error("error", error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const clear = () => {
    setQuery("")
    clearMessageState()
  }

  const noQuestionsYet = messageState.messages.length < 2

  return {
    query,
    setQuery,
    loading,
    messageState,
    setMessageState,
    clearMessageState,
    handleSubmit,
    clear,
    noQuestionsYet
  }
}
